import React, { useState } from 'react';

const ReviewCard = ({ title, children }) => (
  <div className="rounded-xl border overflow-hidden mb-4" style={{ borderColor: '#E5E7EB' }}>
    <div className="px-5 py-3 border-b" style={{ background: '#0B1F3B', borderColor: '#1a3a6b' }}>
      <h3 className="text-sm font-bold tracking-wide uppercase" style={{ color: '#D4A520' }}>{title}</h3>
    </div>
    <div className="px-5 py-4 bg-white">{children}</div>
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs font-medium" style={{ color: '#6B7280' }}>{label}</span>
    <span className="text-xs font-semibold text-right max-w-[60%]" style={{ color: '#0B1F3B' }}>{value || '—'}</span>
  </div>
);

export default function Step4Review({ formData, onSubmit, onBack, submitting, submitError, onRetry }) {
  const [confirmAccurate, setConfirmAccurate] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [checkboxErrors, setCheckboxErrors] = useState({ accurate: false, terms: false });

  const canSubmit = confirmAccurate && confirmTerms && !submitting;

  const handleSubmitClick = () => {
    const errs = { accurate: !confirmAccurate, terms: !confirmTerms };
    if (!confirmAccurate || !confirmTerms) {
      setCheckboxErrors(errs);
      return;
    }
    setCheckboxErrors({ accurate: false, terms: false });
    onSubmit(confirmAccurate, confirmTerms);
  };

  const isDuplicateError = submitError?.toLowerCase()?.includes('duplicate') ||
    submitError?.toLowerCase()?.includes('already exists') ||
    submitError?.toLowerCase()?.includes('unique constraint');

  const isNetworkError = submitError?.toLowerCase()?.includes('network') ||
    submitError?.toLowerCase()?.includes('fetch') ||
    submitError?.toLowerCase()?.includes('connection');

  return (
    <div>
      <style>{`
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeInDown { animation: fadeInDown 0.3s ease-out forwards; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeInUp 0.2s ease-out forwards; }
      `}</style>
      {/* Failure Banner */}
      {submitError && (
        <div className="mb-6 p-4 rounded-xl border animate-fadeInDown" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FEE2E2' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
                <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex-1">
              {isDuplicateError ? (
                <>
                  <p className="text-sm font-bold" style={{ color: '#DC2626' }}>Application Already Exists</p>
                  <p className="text-sm mt-0.5" style={{ color: '#EF4444' }}>An application with this email already exists. Contact <span className="font-semibold">support@wininvest.com</span> if you need to update your application.</p>
                </>
              ) : isNetworkError ? (
                <>
                  <p className="text-sm font-bold" style={{ color: '#DC2626' }}>Connection Error</p>
                  <p className="text-sm mt-0.5" style={{ color: '#EF4444' }}>Unable to connect. Please check your internet connection and try again.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold" style={{ color: '#DC2626' }}>Submission Failed</p>
                  <p className="text-sm mt-0.5" style={{ color: '#EF4444' }}>Submission failed. Please check your connection and try again.</p>
                </>
              )}
            </div>
            {!isDuplicateError && (
              <button
                onClick={onRetry}
                className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors hover:bg-red-100"
                style={{ borderColor: '#EF4444', color: '#EF4444' }}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#0B1F3B', fontFamily: 'Cormorant Garamond, serif' }}>Review Your Application</h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>Step 4 of 4 — Please review all information before submitting</p>
      </div>
      <ReviewCard title="Founder Information">
        {formData?.profilePhotoPreview && (
          <div className="flex justify-center mb-4">
            <img src={formData?.profilePhotoPreview} alt="Profile" className="w-16 h-16 rounded-full object-cover border-4" style={{ borderColor: '#D4A520' }} />
          </div>
        )}
        <ReviewRow label="Full Name" value={formData?.fullName} />
        <ReviewRow label="Email" value={formData?.email} />
        <ReviewRow label="Phone" value={formData?.phone} />
        <ReviewRow label="LinkedIn" value={formData?.linkedinUrl} />
        <ReviewRow label="Location" value={formData?.city && formData?.state ? `${formData?.city}, ${formData?.state}` : ''} />
      </ReviewCard>
      <ReviewCard title="Business Details">
        <ReviewRow label="Business Name" value={formData?.businessName} />
        <ReviewRow label="Website" value={formData?.websiteUrl} />
        <ReviewRow label="Sector" value={formData?.sector} />
        <ReviewRow label="Funding Stage" value={formData?.fundingStage} />
        <ReviewRow label="Year Founded" value={formData?.yearFounded} />
        <ReviewRow label="Employees" value={formData?.employees} />
        <div className="py-1.5">
          <span className="text-xs font-medium block mb-1" style={{ color: '#6B7280' }}>Description</span>
          <p className="text-xs" style={{ color: '#0B1F3B' }}>{formData?.description}</p>
        </div>
      </ReviewCard>
      <ReviewCard title="Investment Ask">
        <ReviewRow label="Minimum Investment" value={formData?.minInvestment ? `$${Number(formData?.minInvestment)?.toLocaleString()}` : ''} />
        <ReviewRow label="Funding Goal" value={formData?.fundingGoal ? `$${Number(formData?.fundingGoal)?.toLocaleString()}` : ''} />
        <ReviewRow label="Projected ROI" value={formData?.projectedRoi ? `${formData?.projectedRoi}%` : ''} />
        <ReviewRow label="Investment Timeline" value={formData?.investmentTimeline} />
        <ReviewRow label="Women-Led" value={formData?.isWomenLed ? 'Yes ✓' : 'No'} />
        <ReviewRow label="Pitch Deck" value={formData?.pitchDeckName || 'Not uploaded'} />
        <div className="py-1.5">
          <span className="text-xs font-medium block mb-1" style={{ color: '#6B7280' }}>Use of Funds</span>
          <p className="text-xs" style={{ color: '#0B1F3B' }}>{formData?.useOfFunds}</p>
        </div>
      </ReviewCard>
      {/* Confirmation Checkboxes */}
      <div className="space-y-3 mb-6">
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                checkboxErrors?.accurate ? 'border-red-400 bg-red-50' : ''
              }`}
              style={{
                borderColor: checkboxErrors?.accurate ? '#EF4444' : confirmAccurate ? '#D4A520' : '#D1D5DB',
                background: confirmAccurate ? '#D4A520' : checkboxErrors?.accurate ? '#FEF2F2' : '#fff'
              }}
              onClick={() => { setConfirmAccurate(!confirmAccurate); if (checkboxErrors?.accurate) setCheckboxErrors(p => ({ ...p, accurate: false })); }}
            >
              {confirmAccurate && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l2.5 2.5L10 3" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span className="text-sm" style={{ color: '#374151' }}>I confirm all information provided is accurate and truthful</span>
          </label>
          {checkboxErrors?.accurate && (
            <div className="flex items-center gap-1 mt-1 ml-8 animate-fadeIn">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
                <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p className="text-xs" style={{ color: '#EF4444' }}>You must agree to continue</p>
            </div>
          )}
        </div>
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
              style={{
                borderColor: checkboxErrors?.terms ? '#EF4444' : confirmTerms ? '#D4A520' : '#D1D5DB',
                background: confirmTerms ? '#D4A520' : checkboxErrors?.terms ? '#FEF2F2' : '#fff'
              }}
              onClick={() => { setConfirmTerms(!confirmTerms); if (checkboxErrors?.terms) setCheckboxErrors(p => ({ ...p, terms: false })); }}
            >
              {confirmTerms && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l2.5 2.5L10 3" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span className="text-sm" style={{ color: '#374151' }}>I agree to WIN's Showcase Terms and Conditions</span>
          </label>
          {checkboxErrors?.terms && (
            <div className="flex items-center gap-1 mt-1 ml-8 animate-fadeIn">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
                <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p className="text-xs" style={{ color: '#EF4444' }}>You must agree to continue</p>
            </div>
          )}
        </div>
      </div>
      {/* Required legend */}
      <p className="text-xs mb-4" style={{ color: '#9CA3AF' }}>* Required fields</p>
      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={submitting}
          className="flex-1 py-4 rounded-xl font-bold text-base border-2 transition-all duration-200 hover:bg-gray-50 disabled:opacity-50"
          style={{ borderColor: '#0B1F3B', color: '#0B1F3B' }}
        >
          ← Back
        </button>
        <button
          onClick={handleSubmitClick}
          disabled={submitting}
          className="flex-1 py-4 rounded-xl font-bold text-base transition-all duration-200 disabled:cursor-not-allowed"
          style={{
            background: canSubmit ? '#D4A520' : '#E5E7EB',
            color: canSubmit ? '#0B1F3B' : '#9CA3AF',
            opacity: submitting ? 0.8 : 1
          }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#D4A520" strokeWidth="3" strokeDasharray="30 70"/>
              </svg>
              Submitting...
            </span>
          ) : 'Submit Application'}
        </button>
      </div>
    </div>
  );
}
